
import { cacheService } from '@/lib/cacheService';
import { fetchSpotPricesFromAPI, fetchHistoryFromAPI } from './api';
import { MetalPrice, TimePeriod } from './types';

const CURRENT_PRICES_CACHE_KEY = 'current_metal_prices';
const CURRENT_PRICES_CACHE_TTL = 25 * 1000; // 25s - just under 30s polling interval

const HISTORY_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours

// Singleton Promise per deduplica: evita chiamate API parallele identiche
let pendingFetch: Promise<{gold: MetalPrice, silver: MetalPrice}> | null = null;

export const fetchCurrentPrices = async (): Promise<{gold: MetalPrice, silver: MetalPrice}> => {
  // 1. Check cache sincrono
  const cached = cacheService.get<{gold: MetalPrice, silver: MetalPrice}>(CURRENT_PRICES_CACHE_KEY, CURRENT_PRICES_CACHE_TTL);
  if (cached) {
    return cached.data;
  }

  // 2. Riusa Promise in corso (deduplica)
  if (pendingFetch) {
    return pendingFetch;
  }

  // 3. Nuova chiamata con cleanup in .finally()
  pendingFetch = fetchSpotPricesFromAPI()
    .then(pricesData => {
      if (pricesData && 'gold' in pricesData && 'silver' in pricesData) {
        const prices = { gold: pricesData.gold, silver: pricesData.silver };
        cacheService.set(CURRENT_PRICES_CACHE_KEY, prices, { expiration: CURRENT_PRICES_CACHE_TTL });
        return prices;
      }
      throw new Error('Invalid data structure for current prices from API');
    })
    .finally(() => {
      pendingFetch = null;
    });

  return pendingFetch;
};

export const fetchPriceHistory = async (
  period: TimePeriod
): Promise<{goldHistory: Partial<MetalPrice>[], silverHistory: Partial<MetalPrice>[]}> => {
  const cacheKey = `history_${period}`;
  const cached = cacheService.get<{goldHistory: Partial<MetalPrice>[], silverHistory: Partial<MetalPrice>[]}>(cacheKey, HISTORY_CACHE_TTL);
  if (cached) {
    return cached.data;
  }

  const historyData = await fetchHistoryFromAPI(period);
  if (historyData && 'goldHistory' in historyData && 'silverHistory' in historyData) {
    const history = { goldHistory: historyData.goldHistory, silverHistory: historyData.silverHistory };
    cacheService.set(cacheKey, history, { expiration: HISTORY_CACHE_TTL });
    return history;
  }
  throw new Error(`Invalid data structure for price history for period: ${period}`);
};

export * from './types';
